import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import type { CrmAccountDetail } from '@ip/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { vatStatusLabel, vatStatusPositive } from '@/components/contractors/vatStatus';
import { useAuth } from '@/hooks/useAuth';
import { hasModuleAccess } from '@/lib/permissions';
import { useCrmApi } from '@/services/api';
import { t } from '@/i18n';
import { ContractorMatchDialog } from './ContractorMatchDialog';
import { ContractorSummaryLine } from './ContractorSummaryLine';
import { useCreateContractorFromAccount } from './useCreateContractorFromAccount';

interface Props {
  nip: string;
  account: CrmAccountDetail;
  onChanged: () => void;
}

/**
 * "Kontrahent" panel on the account page — surfaces the link to the
 * accounting-side contractor record (stage 3 of the CRM/contractor
 * unification) and lets a CRM/admin/ksiegowy user create or match one.
 */
export function ContractorLinkCard({ nip, account, onChanged }: Props) {
  const { permissions } = useAuth();
  const { linkContractor } = useCrmApi();
  // `canManage` gates create/match/unlink alike — one computation, in the hook.
  const { canCreate: canManage, creating, handleCreate } = useCreateContractorFromAccount(
    nip,
    account.id,
    { onCreated: onChanged },
  );
  const [unlinking, setUnlinking] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);

  const canOpenContractor = hasModuleAccess(permissions, nip, 'contractors');
  const contractor = account.contractor;

  const handleUnlink = async () => {
    setUnlinking(true);
    try {
      await linkContractor(nip, account.id, null);
      toast.success(t('crm.accounts.contractor.unlinked'));
      setUnlinkOpen(false);
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('errors.saveFailed'));
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('crm.accounts.contractor.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {contractor ? (
          <>
            <div className="space-y-1">
              <ContractorSummaryLine contractor={contractor} />
              {contractor.vatStatus ? (
                <Badge variant={vatStatusPositive(contractor.vatStatus) ? 'secondary' : 'destructive'}>
                  {vatStatusLabel(contractor.vatStatus)}
                </Badge>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {canOpenContractor ? (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/${nip}/contractors/${contractor.id}`}>{t('crm.accounts.contractor.open')}</Link>
                </Button>
              ) : null}
              {canManage ? (
                <Button variant="ghost" size="sm" onClick={() => setUnlinkOpen(true)}>
                  {t('crm.accounts.contractor.unlink')}
                </Button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <p className="text-muted-foreground">{t('crm.accounts.contractor.none')}</p>
            {canManage ? (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={handleCreate} disabled={creating}>
                  {t('crm.accounts.contractor.add')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setMatchOpen(true)} disabled={creating}>
                  {t('crm.accounts.contractor.match.open')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </CardContent>

      <AlertDialog open={unlinkOpen} onOpenChange={(open) => !unlinking && setUnlinkOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('crm.accounts.contractor.unlinkConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('crm.accounts.contractor.unlinkConfirmBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unlinking}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={unlinking}
              onClick={(event) => {
                event.preventDefault();
                handleUnlink();
              }}
            >
              {t('crm.accounts.contractor.unlink')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ContractorMatchDialog
        nip={nip}
        accountId={account.id}
        open={matchOpen}
        onOpenChange={setMatchOpen}
        onLinked={() => {
          setMatchOpen(false);
          onChanged();
        }}
      />
    </Card>
  );
}
